import { useState, useEffect, useCallback, useRef } from 'react';
import { getLessonById, mapApiLessonToLesson } from '../../../api/knowledgeApi';
import type { Lesson } from '../Training/types';

interface UseLessonResult {
  lesson: Lesson | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Хук для загрузки конкретного урока по ID из API
 * @param lessonId - ID урока
 * @param enabled - Включить ли автоматическую загрузку при монтировании
 */
export const useLesson = (
  lessonId: string | number | undefined,
  enabled: boolean = true
): UseLessonResult => {
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const hasAttemptedRef = useRef<boolean>(false);
  const lastLessonIdRef = useRef<string | number | undefined>(undefined);

  const fetchLesson = useCallback(async () => {
    if (!lessonId) {
      setError(new Error('ID урока не указан'));
      setLoading(false);
      return;
    }

    // Предотвращаем повторные запросы с тем же ID
    if (hasAttemptedRef.current && lastLessonIdRef.current === lessonId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      hasAttemptedRef.current = true;
      lastLessonIdRef.current = lessonId;

      const apiLesson = await getLessonById(lessonId);
      const mappedLesson = mapApiLessonToLesson(apiLesson, 0);

      setLesson(mappedLesson);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Неизвестная ошибка при загрузке урока');
      setError(error);
      if (!error.message.includes('404')) {
        console.error('Ошибка при загрузке урока:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    // Сбрасываем флаг при изменении ID
    if (lastLessonIdRef.current !== lessonId) {
      hasAttemptedRef.current = false;
    }
  }, [lessonId]);

  useEffect(() => {
    if (enabled && lessonId) {
      fetchLesson();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, lessonId]);

  const refetch = useCallback(() => {
    hasAttemptedRef.current = false;
    lastLessonIdRef.current = undefined;
    return fetchLesson();
  }, [fetchLesson]);

  return {
    lesson,
    loading,
    error,
    refetch,
  };
};

